// Millimetres, with the symbol: "70 mm". The manuals are metric, so is everything stored, and so
// is everything shown — there was an inch view once, for supplier catalogues, and nobody used it.
export const formatMm = (mm: number): string => `${mm} mm`;

// How the manual's measured values read in the info table.
//
// The model transcribes what the page says, verbatim and on purpose — it is told to
// leave a field empty rather than invent a value. So these strings are the manual's
// typography, not ours: "15min", "120kg", "1 h 30", "max 50kg". Only the display is
// normalized, never the stored answer, because the answer is a quote.

// The number of minutes, if the whole value is nothing but a minute count. Null for
// every other shape — "1 h 30" is a time this file has no opinion about, and mangling
// it would be worse than leaving it as the manual wrote it.
//
// Shared, because two screens ask this same question and would otherwise each carry
// their own idea of what a minute count looks like: the Phase 1 table abbreviates it,
// the public splash spells it out.
export function bareMinutes(time: string | null | undefined): number | null {
  const m = /^(\d+)\s*min(?:ute)?s?$/i.exec((time || '').trim());
  return m ? Number(m[1]) : null;
}

// For the admin table: "15min" -> "15 mins". Anything that isn't a bare minute count
// is passed through untouched.
export function formatTime(time: string): string {
  const n = bareMinutes(time);
  if (n === null) return (time || '').trim();
  return `${n} min${n > 1 ? 's' : ''}`;
}

// A weight, breathing: "120kg" -> "120 kg", "max 50KG" -> "max 50 kg".
//
// Only the space and the symbol's case change. Whatever words the manual put around
// the number stay — "max" is the page's and not ours to drop — and a unit this does
// not know stays exactly as printed rather than being guessed at.
export function formatWeight(weight: string): string {
  return (weight || '')
    .trim()
    .replace(/(\d)\s*(kgs?|lbs?)\b/gi, (_, n: string, unit: string) => `${n} ${unit.toLowerCase()}`);
}

// One axis of a part's real size, in millimetres, or nothing.
//
// One axis at a time because the table gives each its own row. The single-line form this
// replaces had to write a missing axis as "?", since position was the only thing saying
// which axis a number was; a row carries its own label, so an unknown one is simply blank
// and the "?" that meant "there is a gap here" has nothing left to explain.
// A part's size cell, as the stored object laid out one key per line.
//
// The attribute itself rather than a rendering of it: this row is where you check what was
// actually written down, and a tidied summary is exactly what you cannot check against. So
// the keys keep their names and their order, and the spec is shown as the manual printed
// it — "M6 X 70MM", caps and all.
//
// A null value writes the key and stops. It is not dropped: the absent halves are the
// shape of the answer. Axes with no spec is a part measured off the drawing; a spec with
// no axes is one the manual described and no drawing dimensioned, and a row that hid the
// empty keys would make those two look like the same kind of record.
//
// `source` and `note` are stored but NOT shown. They are prose about one part, and prose
// in a column that is one of thirteen turned a four-line cell into a fifteen-line one and
// pushed everything below the fold. They are the provenance of the numbers rather than the
// numbers, which makes them something to consult and not something to read past.
const SHOWN_KEYS = ['x', 'y', 'z', 'spec'] as const;

// A part that FITS a fastener without being one: a washer, a spacer. Its designation
// is the hole through it, so it is shown as what it is and never beside a length — there
// is none, and the outside is not derivable from it either.
const FITS_KEYS = ['forScrew', 'spec'] as const;

// A BARREL NUT is both of those at once, which is why it gets a list rather than
// borrowing one. Its "M6" is a for_screw — the thread is female, cut to take somebody
// else's screw — and its body is a cylinder, so it has the two extents a supplier prints
// and no third: "Lg." along the barrel, "Body Dia." across it. Three axes would write the
// diameter twice and call one of them a depth.
//
// Neither extent follows from the M6 as a designation, but one of them follows from the
// market: the diameter is ~10 mm for any M6 barrel nut because that is what is made, while
// the length is whatever panel it crosses — McMaster lists five lengths under one thread.
// Which is exactly the split the hardware estimate answers on. So the diameter fills from a
// printed spec, a STEP file or that estimate, and the length waits for one of the first two.
const BARREL_KEYS = ['forScrew', 'lengthMm', 'bodyDiaMm', 'spec'] as const;

// A SCREW is that same shape once more — a designation, two extents and the quote — plus
// the one thing about a screw that is not a measurement at all.
//
// It is also what a threaded fastener gets FULL STOP, Fasteners having been retired into
// it: that category's rows were a thread, a length and the quote, which is this list with
// the head and the drive taken out. Three empty axes above a spec was what a screw's cell
// said before either existed, and those axes were never missing data — they are the wrong
// question for a screw, and no dimension read will ever fill them.
//
// Its designation is its own thread, and its extents are the length it goes in and the head
// that stops it: a supplier's "Lg." and "Dia.". The head is the half no designation fixes,
// exactly as a barrel nut's body is. Nothing in "M4" says 8 mm across; the market does.
//
// WHAT IS NOT HERE is as much of the answer as what is. The head's HEIGHT follows from its
// diameter and the countersink angle, so storing it invites two numbers that disagree. The
// ANGLE is a constant wearing a field's clothes — 90° metric, 82° imperial, and a
// supplier's table prints 82° down every row of every gauge. PITCH is a ratio of the
// diameter for a wood thread rather than an independent fact, and it is the one number no
// manual prints and no photograph can show. And full-versus-partial THREADING is a
// proportion, not a millimetre: a 32 mm chipboard screw is threaded to the head, and where
// that is wrong it is wrong visibly, in a drawing somebody is looking at.
//
// `drive` earns its place by being none of those: it is not derivable from anything else
// here, it is drawn in most manuals, and it is the feature the mesh repair path was built
// to cut. Being a name rather than a number it costs nothing in the estimate loop — see
// measurableKeys, which filters to the millimetres.
const SCREW_KEYS = ['thread', 'lengthMm', 'headDiaMm', 'drive', 'spec'] as const;

// A DOWEL IS TWO MEASUREMENTS AND NOTHING ELSE, which is what makes it the shortest list
// here. Unthreaded and unheaded — nothing turns it, nothing tightens it — so there is no
// designation to carry: a supplier lists dowel pins under a diameter, and each diameter
// under a column of lengths, and those two numbers ARE the part.
//
// Not the three axes either, and this is the whole reason it is not simply left in the
// catch-all. A cylinder has two extents, and a box would write the diameter twice and call
// one of them a depth — the same argument that gave a barrel nut its own list.
//
// A manual printing "M8 X 40MM" on a dowel is naming those two: 8 mm across, 40 mm long.
// The M is the manual's, not a thread's, and `thread` stays empty for a part nothing
// screws into.
const DOWEL_KEYS = ['lengthMm', 'bodyDiaMm', 'spec'] as const;

// A PROTECTIVE FOOT is that same cylinder lying down: a felt pad, a glide, a chair tip. Two
// extents and the quote once more, which is the dowel's list with the order turned around,
// and the order is the whole of what is different.
//
// A supplier prints "Base Dia." first and "Ht." second, because the diameter is what you
// ORDER by — a pad is chosen to cover the leg it goes under, and its thickness is whatever
// that grade of felt comes in. So the diameter leads here where the length leads on a
// dowel, these labels being the supplier's columns and looking a part up in one being what
// they are for.
//
// It is the SQUAT cylinder, and that is not merely a description: 20 across by 4 thick puts
// the axial extent on the SHORT axis, where a dowel and a screw both put it on the long
// one. See wantedDims in meshAnnotations, which places these two numbers on a mesh and had
// to stop assuming the long way is the length to get a pad right.
const FOOT_KEYS = ['bodyDiaMm', 'lengthMm', 'spec'] as const;

// A FASTENER COVER — a wood button, a hole plug, a cover cap — is a turned cap that hides a
// screw head once it is driven. Three measurements and the quote, and no designation at
// all: nothing about it is threaded, nothing screws into it, and no standard names one.
//
// `bodyDiaMm` is the head you SEE, which is the number a manual prints when it prints
// anything ("14.5MM"). `fitsHoleMm` is the bore the plug drops into, which is the number
// you ORDER by — and the two are independent, a 14.5 mm head capping an 8 or a 10 mm hole
// depending on who made it. `lengthMm` is the whole thing, head and plug together.
//
// Not the three axes, which is what it had while it sat in the catch-all: an estimate was
// asked for a bounding box, compared a button against a chair leg in a photograph, and
// answered 12 by 6 by 12 for a part the manual says is 14.5 across.
const COVER_KEYS = ['bodyDiaMm', 'fitsHoleMm', 'lengthMm', 'spec'] as const;

// A HEX KEY, AS McMASTER'S OWN TABLE SIZES ONE: Drive Size, Long Leg, Short Leg.
//
// Three numbers, and the trimming is theirs rather than mine. Their page prints an
// "Overall" column beside "Long Leg" and the two are equal on every row — an L-key's
// longest measurement IS its long leg — so `lengthMm` carries both and nothing has to be
// kept in agreement with anything. "Relative (Short/Long)" is how a catalogue page sorts
// itself, not a fact about the tool. "Finish (black oxide / chrome plated)" is the
// material question, which this app answers somewhere else entirely.
//
// And there is no "for screw" column, which is the answer to the thing that made this
// awkward: a key is identified by the hex it turns, not by the thread behind it.
const HEX_KEY_KEYS = ['driveSizeMm', 'lengthMm', 'shortLegMm', 'spec'] as const;

// What each key is called in the cell. The stored names are the API's; these are the
// words a person reads, and both are the words a SUPPLIER TABLE uses for the same
// columns, because looking a part up in one is what these values are for.
//
// "Thread size" and not "thread diameter": M6 is a designation and not a measurement.
// It names a diameter AND a pitch — written out it is M6x1 — and the coarse pitch is
// implied only because the standard implies it. The same field holds "1/4-20", where the
// second half is the pitch in threads per inch, so "diameter" would be a half-truth in
// metric and false in imperial.
//
// "Total length" for the same reason in reverse: on its own "length" is the word a
// supplier table spends on two different columns, the screw's and its thread's.
const KEY_LABELS: Record<string, string> = {
  // THE THREE AXES IN WORDS. They fell through to their own key and read as a coordinate —
  // "x: 482 mm" is a number on an axis whose meaning is somewhere else, and the meaning is
  // fixed: the estimate call defines x as the part's width AS IT SITS IN THE FINISHED
  // PRODUCT, y as its depth and z as its height (see _ESTIMATE_FIELDS). Nothing about the
  // stored size changes; this is what those letters have always meant, said on the row.
  x: 'width',
  y: 'depth',
  z: 'height',
  thread: 'thread size',
  lengthMm: 'total length',
  bodyDiaMm: 'body diameter',
  headDiaMm: 'head diameter',
  fitsHoleMm: 'fits hole',
  // McMaster's own column heading. Not "across flats", which is the machinist's word for
  // the same measurement — these labels are the ones a supplier table uses, because
  // looking the part up in one is what the values are for.
  driveSizeMm: 'drive size',
  shortLegMm: 'short leg',
  drive: 'drive',
  forScrew: 'for screw',
};

// One key in words, for a caller that writes a single measurement somewhere of its own —
// the size on a box's edge in the listing zoom. The same table the size cell reads, so a
// dimension is called the same thing wherever it is written.
export const sizeKeyLabel = (key: string): string => KEY_LABELS[key] ?? key;

// Which keys carry millimetres, so the unit toggle knows what it may convert. `thread` is
// not one of them because it is not a number: it is a designation, and formatThread does
// the same job for it — an M6 asked about in inches is its diameter, 0.236".
const MM_KEYS = new Set(['x', 'y', 'z', 'lengthMm', 'bodyDiaMm', 'headDiaMm', 'fitsHoleMm',
  'driveSizeMm', 'shortLegMm']);

// THE TWO ENDS OF AN AXIS, bottom then top, for the two axes a drawing prints them on. Not rows
// of their own and not measurable keys: they are the shape inside `x` and `y`, written in
// those rows, and no estimate is ever asked for them.
const ENDS: Partial<Record<string, readonly [SizeKey, SizeKey]>> = {
  x: ['xBottom', 'xTop'],
  y: ['yBottom', 'yTop'],
};

// WHICH ROWS A CATEGORY GETS. The default is the three axes and the quote, and a category
// earns a different list by being a kind of thing three axes do not describe: a screw is
// identified by its thread and its length, a washer by the screw it fits.
//
// By CATEGORY and not by which fields happen to be filled, which is what this used to do
// and how a wooden dowel came to be laid out as a screw — it had a length, so it got the
// fastener rows, blank "thread size" and all. What a part IS decides how it is described;
// what is known about it decides only how much of that description is filled in.
export type SizeKey = keyof NonNullable<PartSizeish>;

const KEYS_BY_CATEGORY: Record<string, readonly SizeKey[]> = {
  screw: SCREW_KEYS,
  screws: SCREW_KEYS,
  dowel: DOWEL_KEYS,
  dowels: DOWEL_KEYS,
  'fastener cover': COVER_KEYS,
  'fastener covers': COVER_KEYS,
  washer: FITS_KEYS,
  washers: FITS_KEYS,
  'barrel nut': BARREL_KEYS,
  'barrel nuts': BARREL_KEYS,
  'protective foot': FOOT_KEYS,
  'protective feet': FOOT_KEYS,
  // THE WHOLE TOOLS FOLDER, because in a flat-pack manual it holds one thing. Every
  // notice here ships an Allen key and nothing else, and three axes describe an L-shaped
  // bar no better than they describe a screw. A spanner or a screwdriver arriving one day
  // wants its own folder rather than a fourth reading of these three rows.
  tool: HEX_KEY_KEYS,
  tools: HEX_KEY_KEYS,
  'allen key': HEX_KEY_KEYS,
  'allen keys': HEX_KEY_KEYS,
  'hex key': HEX_KEY_KEYS,
  'hex keys': HEX_KEY_KEYS,
};

// A category's MEASURABLE rows: the ones that hold millimetres, so the ones an estimate
// could ever answer. Its designations are excluded and that is the whole distinction —
// "M6" is a name a standard gives a thread, and no amount of looking at a photograph
// produces a name. A measurement is a thing you can be right about by comparing.
//
// Which makes Washers a category with nothing to estimate: `for screw` is its only row
// besides the quote, and it is a designation. That is correct rather than a gap — the
// outside of a washer is not in its designation, and nothing here claims to know it.
// THE SIZE AS THIS CATEGORY DESCRIBES IT, with everything else dropped.
//
// A part keeps whatever any reading ever wrote on it, and readings were taken under other
// categories. A wood button estimated while it sat in the catch-all was answered as a
// bounding box — 12 by 12 by 6 — and refiling it under Fastener covers did not delete those
// axes, it only stopped the table from showing them. Anything else reading the size object
// went on finding them.
//
// Which is not a table's problem to solve twice: the 3D viewer labelled the mesh "wanted 12
// mm" from numbers nothing on screen admitted to holding, because wantedDims returns axes
// untouched the moment it finds any. A category says which measurements DESCRIBE a part,
// and a measurement it does not name is not this part's size in any smaller sense — so it
// is not passed on.
//
// The designations ride along: `spec` is what a supplier calls the part and every category
// shows one, and `source`/`note` are about the reading rather than about a dimension.
// WHAT SURVIVES A CATEGORY IT DOES NOT BELONG TO.
//
// `source` and `note` are about the reading rather than about a dimension, and every
// category shows a `spec`. `lengthMm` is here for a different reason: it is the one number
// that ANCHORS A MESH when no axis is known (see mmPerUnit, which falls back to it against
// the longest extent), and a part whose category is not in the table above gets the default
// three axes — so a screwdriver measured off the manual's drawings had its only measurement
// dropped on the way to the viewer and the model stayed at file scale. It is also not the
// key a stale reading leaves behind: that case is a bounding box written under a category
// where a part IS a bounding box, which is x, y and z.
const CARRIED = new Set(['source', 'note', 'lengthMm']);

export function sizeForCategory<T extends PartSizeish>(size: T, category = ''): T {
  if (!size) return size;
  const keep = new Set<string>(KEYS_BY_CATEGORY[category.trim().toLowerCase()] ?? SHOWN_KEYS);
  // An axis's ends go where the axis goes: they are the same measurement, read closer.
  for (const [axis, ends] of Object.entries(ENDS)) {
    if (keep.has(axis)) ends?.forEach((e) => keep.add(e));
  }
  const out = { ...size };
  for (const key of Object.keys(out)) {
    if (!keep.has(key) && !CARRIED.has(key)) {
      (out as Record<string, unknown>)[key] = null;
    }
  }
  return out;
}

export const measurableKeys = (category = ''): SizeKey[] =>
  (KEYS_BY_CATEGORY[category.trim().toLowerCase()] ?? SHOWN_KEYS).filter((k) => MM_KEYS.has(k));

const isThreaded = (size: PartSizeish) => !!size?.thread;
const hasLength = (size: PartSizeish) => size?.lengthMm != null;
const hasBodyDia = (size: PartSizeish) => size?.bodyDiaMm != null;
const hasHeadDia = (size: PartSizeish) => size?.headDiaMm != null;
const hasDrive = (size: PartSizeish) => !!size?.drive;


// The keys are drawn even when there is no size object at all. A part nothing dimensions
// and a part nobody has read are both "no size", and both should look like the row they
// are in — a blank cell beside four labelled blanks reads as a rendering fault rather
// than as an answer.
export function formatPartSize(size: PartSizeish, category = ''): string {
  // The DATABASE's category, passed in by the caller. A part's category is a fact about
  // the part, and the database is where a part's facts live — the model's guess on one
  // notice is how it got there, not what it is.
  const keys = KEYS_BY_CATEGORY[category.trim().toLowerCase()] ?? SHOWN_KEYS;
  return keys
    .map((k) => {
      const v = size?.[k] ?? '';
      let shown: string | number = v;
      if (MM_KEYS.has(k)) shown = formatMeasure(size, k);
      return `${KEY_LABELS[k] ?? k}: ${shown}`;
    })
    .join('\n');
}

// ONE MEASUREMENT AS THE TABLE WRITES IT: "482 mm", "~482 mm", or "453 → 482 mm" where the
// drawing printed both ends of the axis. Empty where nothing is known. Shared by the parts'
// cells and the product's, so a width reads the same in either column.
export function formatMeasure(
  size: Partial<NonNullable<PartSizeish>> | null | undefined, key: SizeKey,
): string {
  // An estimate wears a ~. It was reasoned — off a photograph against a part that WAS
  // measured, or off the standard a piece of hardware was made to — which is worth having and
  // is not the same kind of number as the one beside it. `source` has carried that
  // distinction, unshown, since the day a second call could write here.
  const about = size?.source === 'derived' ? '~' : '';
  // THE ENDS FIRST, where both are there: they say the box's figure as well (it is the larger
  // of the two) and the shape it hides. Shown even with no `x` beside them — a pair is two
  // printed numbers, and hiding them for want of a third would hide a reading.
  const ends = ENDS[key];
  const bottom = ends ? size?.[ends[0]] : null;
  const top = ends ? size?.[ends[1]] : null;
  if (typeof bottom === 'number' && typeof top === 'number') {
    return `${about}${bottom} → ${top} mm`;
  }
  const v = size?.[key];
  return typeof v === 'number' ? about + formatMm(v) : '';
}

// A part's size for a PROMPT: "430 x 40 x 25 mm.", "M6 x 70 mm." for a fastener, or the
// printed spec when that is all there is. Unknown values are dropped rather than written
// "?", because this is read by a model that would take the ? for a value.
export function formatDims(size: PartSizeish): string {
  if (isThreaded(size) || hasLength(size) || hasBodyDia(size) || hasHeadDia(size)
      || hasDrive(size)) {
    // The breakdown and not the quote, because a model reads this: "M6 x 70 mm" is the
    // same fact as "M6 X 70MM", written the way a supplier writes it.
    // "M6 x 70 mm" for a screw, "40 mm" for a dowel: the designation joins the length
    // when there is one, and is simply absent when there is not.
    //
    // A barrel nut's designation is its forScrew rather than its thread, and it reads the
    // same way round here: what the reader wants first is which screw the thing takes.
    const head = [
      size?.thread || size?.forScrew,
      size?.lengthMm != null ? `${size.lengthMm} mm` : null,
    ].filter(Boolean).join(' x ');
    // A diameter is not a third factor of that product — a barrel nut is not 15 by 10 — so
    // each is appended as its own measurement, with the word that says which it is.
    //
    // The head's says whose, because a screw is the one part here with two diameters:
    // "M4 x 32 mm, diameter 8 mm" would read as the thread measured out, which is the one
    // number that sentence already gives. And the drive last, as the only thing in the line
    // that is a name rather than a size.
    const full = [
      head,
      size?.bodyDiaMm != null ? `diameter ${size.bodyDiaMm} mm` : null,
      size?.headDiaMm != null ? `head diameter ${size.headDiaMm} mm` : null,
      size?.drive ? `${size.drive} drive` : null,
    ].filter(Boolean).join(', ');
    if (full) return `${full}.`;
  }
  const known = [size?.x, size?.y, size?.z].filter((v): v is number => v != null);
  if (known.length) return `${known.join(' x ')} mm.`;
  return size?.spec ? `${size.spec}.` : '';
}

type PartSizeish = {
  x: number | null; y: number | null; z: number | null; spec: string | null;
  thread?: string | null; lengthMm?: number | null; bodyDiaMm?: number | null;
  headDiaMm?: number | null; fitsHoleMm?: number | null; drive?: string | null;
  driveSizeMm?: number | null; shortLegMm?: number | null;
  forScrew?: string | null;
  xBottom?: number | null; xTop?: number | null; yBottom?: number | null; yTop?: number | null;
  source?: 'measured' | 'derived' | null; note?: string | null;
} | null | undefined;

// A part's size, as a size and not as a shout: "M6 X 70MM" -> "M6 x 70 mm", "20MM" ->
// "20 mm".
//
// Two rules, both about the caps a manual sets its hardware table in. The dimension
// symbol between two numbers is a times sign, which is lowercase everywhere but on a
// manual. And a unit is a symbol — "mm", "cm" — never "MM"; it takes a space from its
// number, the way every other measurement here does.
//
// A thread code keeps its capital ("M6", "M8"): the M is part of the name of the
// thread, not a unit, which is why the unit rule wants a DIGIT in front of it.
//
// It takes the raw printed string and not the broken-down size, because its caller is
// the image SEARCH: what is wanted there is the manual's own words, tidied.
export function formatSize(size: string): string {
  return (size || '')
    .trim()
    // "M6X70" and "20X30": the symbol is wedged against its numbers.
    .replace(/(\d)\s*[x×]\s*(?=[\dØø])/gi, '$1 x ')
    // "70MM X 20MM": the same symbol, standing alone — the rule above cannot see it,
    // because what precedes it there is a unit rather than a digit.
    .replace(/(\s)[X×](\s)/g, '$1x$2')
    .replace(/(\d)\s*(mm|cm|m)\b/gi, (_, n: string, unit: string) => `${n} ${unit.toLowerCase()}`);
}
